import { Dialog, DialogContent, Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BGImage from "assets/wizardCheckList.png";
import { postUserVisit } from "components/app/App.slice";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getHltInncodes } from "utils/CommonFunctions";
import { ON_BOARDING } from "utils/URLConstants";
import { commonApiCallParam, updatePopupHeader } from "utils/UserAnalyticsUtils";
import { triggerNeverLoggedInWarning } from "./Helper";
import { fetchOnboardingQueries, reset } from "./Onboarding.slice";
import StepContent from "./StepContent";
import WelcomeWizard from "./WelcomeWizard";

const flow: any = {
  "1": "Onboarding Wizard - Step1",
  "2": "Onboarding Wizard - Step2",
  "3": "Onboarding Wizard - Step3"
};

function Index() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const translate = useTranslate();
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);

  const {
    showOnboardingWizardFromLibrary,
    checkApiResponse,
    Onboarding: onboardingDetails
  } = useAppSelector((state) => state.Onboarding);

  const { createdDate, isNewFocusAreaAvailable, isFocusAreasExpired, retakeWizardRequired } = checkApiResponse;

  const [steps, setSteps] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number | null>(null);

  useEffect(() => {
    const isUserDashboardPage = location.pathname.split("/")[1] === "userDashboard";
    if (!isUserDashboardPage) triggerNeverLoggedInWarning(dispatch);
  }, [isImpersonated, checkApiResponse, location.pathname]);

  const showWizard = useMemo(() => {
    if (showOnboardingWizardFromLibrary) {
      return isImpersonated ? !!createdDate : true;
    }
    return !isEmpty(checkApiResponse) && !isImpersonated && (!createdDate || retakeWizardRequired);
  }, [showOnboardingWizardFromLibrary, isImpersonated, checkApiResponse]);

  const getOnboardingQuestions = async () => {
    const fetchAllQuestions = isImpersonated ? false : isFocusAreasExpired || !createdDate;

    if (fetchAllQuestions) {
      setCurrentPage(1);
    } else {
      setCurrentPage(2);
    }

    await dispatch(
      fetchOnboardingQueries({
        wizardType: ON_BOARDING,
        payload: {
          fetchAllQuestions,
          innCode: getHltInncodes(true)
        }
      })
    );
  };

  useEffect(() => {
    if (showWizard) {
      getOnboardingQuestions();
    }
  }, [showOnboardingWizardFromLibrary, checkApiResponse]);

  useEffect(() => {
    let params = commonApiCallParam(true);
    let updatedparam = "";
    if (showWizard && onboardingDetails.questionnaire) {
      if (showOnboardingWizardFromLibrary) {
        updatedparam = updatePopupHeader("Retake Wizard");
      } else {
        updatedparam = updatePopupHeader(flow[steps] ? `${flow[steps]}` : "Onboarding Wizard");
      }
      params.popupHeader = updatedparam;
      dispatch(postUserVisit(params));
    }
  }, [steps, showWizard, onboardingDetails]);

  return (
    <>
      {showWizard && onboardingDetails.questionnaire && (
        <Dialog
          fullScreen={matches}
          maxWidth={"md"}
          open={true}
          onClose={(e, reason) => {
            if (reason && reason === "backdropClick") return;
            dispatch(reset());
          }}
        >
          <DialogContent sx={{ display: "flex", padding: 0 }}>
            <Grid container>
              <Grid
                item
                md={5}
                xs={0}
                sx={{
                  margin: 0,
                  backgroundPositionX: "40%",
                  backgroundPositionY: "calc(100% - 100px)",
                  backgroundImage: `url(${BGImage})`,
                  backgroundColor: tokens.Border.General.Color,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "50%",
                  color: tokens.Text.General.Inverse,
                  width: "400px",
                  display: {
                    xs: "none",
                    sm: "none",
                    md: "inherit",
                    minHeight: 450,
                    justifyContent: "center"
                  }
                }}
              >
                <Typography sx={{ padding: "50px 60px" }} variant={"h5"} showBold>
                  {currentPage === 1
                    ? `Hilton BOOST`
                    : `
                  ${isNewFocusAreaAvailable && !isFocusAreasExpired ? translate("onboarding.new.focusAreas") : ""} 
                 ${
                   retakeWizardRequired || showOnboardingWizardFromLibrary
                     ? translate("onboarding.preferences")
                     : translate("onboarding.setPreferences")
                 }
                  
                 `}
                </Typography>
              </Grid>
              <Grid
                container
                sx={{ padding: "16px", minHeight: 500 }}
                width="100%"
                item
                md={7}
                display={"flex"}
                flexDirection="column"
                alignItems="center"
                justifyContent={"space-around"}
              >
                {currentPage === 1 ? (
                  <WelcomeWizard setCurrentPage={setCurrentPage} />
                ) : (
                  <StepContent onboardingDetails={onboardingDetails} onStepChange={(step) => setSteps(step)} />
                )}
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default Index;
